import { portalApi } from "./client";
import type { PortalBatchResult } from "./batch";

// TPS Servicing Portal PR-I (WS4) — portal-side client + DTO types for the
// firm's "Tax forms" MANAGEMENT surface: the payer's 1099 lifecycle (select →
// prefill → edit → approve (locks) → send → optional IRS e-file). Mirrors the C#
// records the backend exposes under /api/portal/tps/tax-forms. Kept inside the
// portal bundle so nothing here imports staff types. No React Query in the
// portal — callers load with useState and re-run the loader after a mutation.

/** A 1099 information return as seen by the owning TPS. `taxIdMasked` shows the
 *  last four (`***-**-1234`); the unmasked value only comes back from the
 *  ownership-gated reveal path, never on a list. */
export interface TaxForm {
  id: string;
  tpsId: string;
  taxYear: number;
  formType: string;
  formTitle: string;
  recipientType: string;
  recipientId: string;
  loanId: string | null;
  poolId: string | null;
  recipientName: string;
  recipientEmail: string | null;
  taxIdMasked: string | null;
  hasTaxId: boolean;
  grossAmount: number;
  box1Amount: number;
  box1Label: string;
  /** Additional-box values keyed by the form-type's box keys (currency stored
   *  as a plain string the editor parses). */
  formData: Record<string, string> | null;
  status: string;
  isLocked: boolean;
  isInformational: boolean;
  hasDocument: boolean;
  supersedesId: string | null;
  preparedByUserId: string | null;
  preparedAt: string | null;
  approvedAt: string | null;
  sentAt: string | null;
  eFileStatus: string;
  eFileAuthorizedAt: string | null;
  createdAt: string;
}

/** A computed prefill candidate — a form the system thinks the firm owes a
 *  recipient for the year, with a suggested amount. Created on selection. */
export interface TaxFormCandidate {
  formType: string;
  formTitle: string;
  recipientType: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string | null;
  hasTaxIdOnFile: boolean;
  suggestedGrossAmount: number;
  suggestedBox1Amount: number;
  loanId: string | null;
  alreadyCreated: boolean;
}

/** One additional box on a form type (beyond Box 1). */
export interface TaxFormBox {
  key: string;
  label: string;
  isCurrency: boolean;
}

/** One form-library entry for the FE picker (the registry, projected). */
export interface TaxFormType {
  formType: string;
  title: string;
  defaultRecipientType: string;
  isInformational: boolean;
  hasAutoPrefill: boolean;
  box1Label: string;
  additionalBoxes: TaxFormBox[];
}

/** Create a form — from a computed candidate or a blank manual template. The
 *  computation re-runs server-side to populate amounts. */
export interface TaxFormCreateRequest {
  taxYear: number;
  formType: string;
  recipientType: string;
  recipientId: string;
  loanId?: string | null;
  poolId?: string | null;
  supersedesId?: string | null;
}

/** Edit a Draft form field-by-field. null/undefined = leave unchanged; "" (for
 *  taxId) = clear. Rejected once Approved (locked). */
export interface TaxFormEditRequest {
  recipientName?: string | null;
  recipientEmail?: string | null;
  taxId?: string | null;
  grossAmount?: number | null;
  box1Amount?: number | null;
  formData?: Record<string, string> | null;
}

/** The single-field reveal payload from the ownership-gated TIN-reveal path. */
interface TaxIdReveal {
  taxId: string | null;
}

export const tpsTaxFormsApi = {
  list: (taxYear?: number) =>
    portalApi.get<TaxForm[]>("/tps/tax-forms", taxYear ? { taxYear } : undefined),
  candidates: (taxYear: number) =>
    portalApi.get<TaxFormCandidate[]>("/tps/tax-forms/candidates", { taxYear }),
  library: () => portalApi.get<TaxFormType[]>("/tps/tax-forms/library"),
  get: (id: string) => portalApi.get<TaxForm>(`/tps/tax-forms/${id}`),
  /** Reveal the recipient's full TIN to the owning firm. Ownership-gated +
   *  audited server-side. */
  revealTin: (id: string) => portalApi.get<TaxIdReveal>(`/tps/tax-forms/${id}/tax-id`),
  create: (body: TaxFormCreateRequest) => portalApi.post<TaxForm>("/tps/tax-forms", body),
  update: (id: string, body: TaxFormEditRequest) =>
    portalApi.put<TaxForm>(`/tps/tax-forms/${id}`, body),
  approve: (id: string) => portalApi.post<TaxForm>(`/tps/tax-forms/${id}/approve`),
  /** SP-2b — approve + lock up to 50 Draft forms in one call. Per-id results;
   *  an informational form (e.g. 1099-K) fails with its reason per id. */
  batchApprove: (ids: string[]) =>
    portalApi.post<PortalBatchResult>("/tps/tax-forms/batch-approve", { ids }),
  send: (id: string) => portalApi.post<TaxForm>(`/tps/tax-forms/${id}/send`),
  efile: (id: string) => portalApi.post<TaxForm>(`/tps/tax-forms/${id}/efile`),
  /** The Copy B PDF (Print / preview) as a Blob, ownership-scoped to the payer. */
  pdf: (id: string) => portalApi.getBlob(`/tps/tax-forms/${id}/pdf`),
};
